<script lang="ts" setup>
import { computed, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import { nextTick } from 'vue';

const SIZES = {
    xsmall: 460,
    small: 600,
    medium: 900,
    large: 1200,
    xlarge: 1536,
} as const;
type Size = keyof typeof SIZES;

const isOpen = defineModel<boolean>({ required: true });

const props = withDefaults(defineProps<{
    fullscreen?: boolean;
    hideClose?: boolean;
    size?: Size;
    title?: string;
    trapFocus?: boolean;
}>(), {
    fullscreen: false,
    hideClose: false,
    size: 'medium',
    title: undefined,
    trapFocus: true,
});

const { t } = useI18n();

const sizeValue = computed<number>(() => SIZES[props.size]);

const close = () => {
    isOpen.value = false;
};

const dialogRef = useTemplateRef('dialogRef');
const { activate, deactivate } = useFocusTrap(dialogRef, {
    immediate: props.trapFocus,
});

watch(isOpen, (val) => {
    if (!props.trapFocus) return;

    if (val) {
        nextTick(() => activate());
    } else {
        deactivate();
    }
});
</script>

<template>
    <VDialog
        v-model="isOpen"
        :width="!fullscreen ? sizeValue : undefined"
        :fullscreen="fullscreen"
        scrollable
    >
        <VCard
            ref="dialogRef"
            class="d-flex flex-column overflow-hidden pa-0"
            color="background"
        >
            <VCardTitle class="d-flex ga-2 align-center px-6 pt-6 pb-4">
                <div class="flex-1-1">
                    <slot name="title">
                        <span class="text-h3 text-high-emphasis">{{ title }}</span>
                    </slot>
                </div>
                <VBtn
                    v-if="!hideClose"
                    :aria-label="t('close')"
                    color="background"
                    size="small"
                    elevation="0"
                    icon
                    data-testid="buttonClose"
                    @click="close"
                >
                    <VIcon
                        icon="close"
                        size="24"
                    />
                </VBtn>
            </VCardTitle>
            <VCardText class="flex-grow-1 flex-shrink-1 px-6 pt-0 pb-6">
                <div class="h-100">
                    <slot />
                </div>
            </VCardText>
            <VCardActions
                v-if="$slots.actions"
                class="pa-0 d-block"
            >
                <VDivider />
                <div class="px-6 py-4 d-flex align-center justify-end ga-2">
                    <slot name="actions" />
                </div>
            </VCardActions>
        </VCard>
    </VDialog>
</template>
